import React, { useState, useEffect, useMemo } from 'react';
import { X, Merge, Search, AlertTriangle, CheckCircle2, Loader2, ArrowRight, UserCheck, Trash2 } from 'lucide-react';
import { searchCustomers, mergeCustomers, handleApiError } from '../../../../services/api/customerApi';
import CustomerAvatar from '../../../../components/common/CustomerAvatar';

const MergeCustomerModal = ({ initialTarget = null, initialDuplicates = [], isOpen, onClose, onSuccess }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // Selected state
    const [targetCustomer, setTargetCustomer] = useState(initialTarget);
    const [duplicateCustomers, setDuplicateCustomers] = useState(initialDuplicates);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setTargetCustomer(initialTarget);
            setDuplicateCustomers(initialDuplicates || []);
            setError(null);
            setSuccessMsg(null);
            setSearchTerm('');
            setSearchResults([]);
        }
    }, [isOpen, initialTarget, initialDuplicates]);

    // Perform live customer search
    useEffect(() => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            return;
        }

        const delayDebounce = setTimeout(async () => {
            setIsSearching(true);
            try {
                const results = await searchCustomers(searchTerm.trim());
                setSearchResults(results || []);
            } catch (err) {
                console.error('Error searching customers for merge:', err);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    if (!isOpen) return null;

    const handleSelectTarget = (customer) => {
        setTargetCustomer(customer);
        // Remove from duplicates if present
        setDuplicateCustomers(prev => prev.filter(c => (c.id || c.publicId) !== (customer.id || customer.publicId)));
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleAddDuplicate = (customer) => {
        const custId = customer.id || customer.publicId;
        const targetId = targetCustomer?.id || targetCustomer?.publicId;

        if (targetId && custId === targetId) {
            setError("Target customer cannot be added as a duplicate to itself");
            return;
        }

        if (!duplicateCustomers.some(c => (c.id || c.publicId) === custId)) {
            setDuplicateCustomers(prev => [...prev, customer]);
        }
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleRemoveDuplicate = (custId) => {
        setDuplicateCustomers(prev => prev.filter(c => (c.id || c.publicId) !== custId));
    };

    const handleExecuteMerge = async () => {
        const targetId = targetCustomer?.id || targetCustomer?.publicId;
        if (!targetId) {
            setError('Please select a Primary / Target Customer');
            return;
        }

        if (!duplicateCustomers || duplicateCustomers.length === 0) {
            setError('Please select at least one Duplicate Customer to merge');
            return;
        }

        const duplicateIds = duplicateCustomers.map(c => c.id || c.publicId);

        setIsSubmitting(true);
        setError(null);

        try {
            await mergeCustomers({
                targetCustomerPublicId: targetId,
                duplicateCustomerPublicIds: duplicateIds
            });

            setSuccessMsg('Customers merged successfully!');
            setTimeout(() => {
                if (onSuccess) onSuccess();
                onClose();
            }, 1200);
        } catch (err) {
            console.error('Failed to merge customers:', err);
            setError(handleApiError(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full flex flex-col overflow-hidden border border-gray-100 animate-in fade-in duration-200">
                
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-blue-950 via-slate-900 to-indigo-950 text-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-blue-500/20 rounded-xl border border-blue-400/30 text-blue-300">
                            <Merge size={22} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                Merge Duplicate Customers
                            </h2>
                            <p className="text-xs text-blue-200/80">
                                Consolidate duplicate records, order history, and balances into a single primary customer.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-blue-200 hover:text-white"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">

                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-xs font-medium">
                            <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {successMsg && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700 text-xs font-medium">
                            <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                            <span>{successMsg}</span>
                        </div>
                    )}

                    {/* Step 1: Target Customer (Master) Selection */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            Primary (Master) Customer — Records Will Be Kept Here
                        </label>

                        {targetCustomer ? (
                            <div className="p-3.5 bg-emerald-50/60 border-2 border-emerald-500/40 rounded-xl flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <CustomerAvatar name={targetCustomer.name || targetCustomer.customerName} size="md" />
                                    <div>
                                        <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                            {targetCustomer.name || targetCustomer.customerName}
                                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-extrabold rounded-md uppercase">
                                                Master Record
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5">
                                            {targetCustomer.customerId && <span>ID: {targetCustomer.customerId}</span>}
                                            {targetCustomer.mobileNumber && <span>Mob: {targetCustomer.mobileNumber}</span>}
                                            {targetCustomer.categoryName && <span>Cat: {targetCustomer.categoryName}</span>}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setTargetCustomer(null)}
                                    className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-100/80 hover:bg-emerald-200 px-3 py-1.5 rounded-lg transition-colors"
                                >
                                    Change Primary
                                </button>
                            </div>
                        ) : (
                            <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl text-center">
                                <p className="text-xs font-medium text-gray-500 mb-2">Search and select the primary customer to keep:</p>
                                <div className="relative max-w-md mx-auto">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Type customer name or mobile..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {isSearching && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Step 2: Search / Add Duplicate Customers */}
                    <div className="space-y-3">
                        <label className="block text-xs font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            Duplicate Customers — Will Be Merged into Primary & Deactivated
                        </label>

                        {/* Search Bar for adding customers if searching */}
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search customer to add as target or duplicate..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            {isSearching && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
                        </div>

                        {/* Search Dropdown Results */}
                        {searchResults.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto divide-y divide-gray-100 z-10">
                                {searchResults.map((cust) => {
                                    const custId = cust.id || cust.publicId;
                                    const isTarget = (targetCustomer?.id || targetCustomer?.publicId) === custId;
                                    const isDuplicate = duplicateCustomers.some(d => (d.id || d.publicId) === custId);

                                    return (
                                        <div key={custId} className="p-2.5 hover:bg-gray-50 flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-2.5">
                                                <CustomerAvatar name={cust.name} size="sm" />
                                                <div>
                                                    <div className="font-semibold text-gray-800">{cust.name}</div>
                                                    <div className="text-[10px] text-gray-400">
                                                        {cust.customerId ? `ID: ${cust.customerId} | ` : ''}
                                                        {cust.mobileNumber ? `Mob: ${cust.mobileNumber}` : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {!targetCustomer && (
                                                    <button
                                                        onClick={() => handleSelectTarget(cust)}
                                                        className="px-2.5 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-lg font-semibold transition-colors"
                                                    >
                                                        Set Primary
                                                    </button>
                                                )}
                                                {!isDuplicate && !isTarget && (
                                                    <button
                                                        onClick={() => handleAddDuplicate(cust)}
                                                        className="px-2.5 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg font-semibold transition-colors"
                                                    >
                                                        + Add Duplicate
                                                    </button>
                                                )}
                                                {isTarget && <span className="text-[10px] font-bold text-emerald-600">Selected as Primary</span>}
                                                {isDuplicate && <span className="text-[10px] font-bold text-amber-600">Selected as Duplicate</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* List of Selected Duplicate Customers */}
                        {duplicateCustomers.length > 0 ? (
                            <div className="space-y-2">
                                {duplicateCustomers.map((dup) => {
                                    const dupId = dup.id || dup.publicId;
                                    return (
                                        <div key={dupId} className="p-3 bg-amber-50/50 border border-amber-200 rounded-xl flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-3">
                                                <CustomerAvatar name={dup.name || dup.customerName} size="sm" />
                                                <div>
                                                    <div className="font-bold text-gray-800 flex items-center gap-2">
                                                        {dup.name || dup.customerName}
                                                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[9px] font-bold rounded">
                                                            To Merge
                                                        </span>
                                                    </div>
                                                    <div className="text-[10px] text-gray-500">
                                                        {dup.customerId ? `ID: ${dup.customerId} | ` : ''}
                                                        {dup.mobileNumber ? `Mob: ${dup.mobileNumber}` : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveDuplicate(dupId)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Remove duplicate from list"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-center text-xs text-gray-400 italic">
                                No duplicate customers selected yet. Use the search bar above to select customers to merge.
                            </div>
                        )}
                    </div>

                    {/* Merge Summary Alert */}
                    {targetCustomer && duplicateCustomers.length > 0 && (
                        <div className="p-4 bg-blue-50/70 border border-blue-200 rounded-xl space-y-2">
                            <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                                <UserCheck size={16} className="text-blue-600" />
                                Merge Impact Summary
                            </h4>
                            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                                <li>
                                    All past orders, invoices, and sales collections from <strong>{duplicateCustomers.length} duplicate customer(s)</strong> will be re-assigned to <strong>{targetCustomer.name || targetCustomer.customerName}</strong>.
                                </li>
                                <li>
                                    Operations log reports and pending collections will show consolidated totals under the primary customer.
                                </li>
                                <li>
                                    Duplicate customer profile(s) will be deactivated to prevent duplicate orders in the future.
                                </li>
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-200/60 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={handleExecuteMerge}
                        disabled={isSubmitting || !targetCustomer || duplicateCustomers.length === 0}
                        className={`
                            px-5 py-2.5 text-xs font-bold text-white rounded-xl shadow-lg flex items-center gap-2 transition-all
                            ${isSubmitting || !targetCustomer || duplicateCustomers.length === 0
                                ? 'bg-gray-300 shadow-none cursor-not-allowed'
                                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/25 active:scale-[0.98]'
                            }
                        `}
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                <span>Merging Customers...</span>
                            </>
                        ) : (
                            <>
                                <Merge size={16} />
                                <span>Confirm & Merge Customers</span>
                            </>
                        )}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default MergeCustomerModal;
